import React from 'react';
import { Row, Col, Tab, Nav, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlus, faUser, faPhone, faEnvelope, faCalendar, faCheck, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { CheckSquare, StickyNote, Calendar, FileCheck } from 'lucide-react';

const ProjectDetailSkeleton = ({ show = true }) => {
  const shimmerStyle = {
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  };

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>

      <div
        className={`offcanvas w-100 p-0 offcanvas-end ${show ? "show" : ""}`}
        tabIndex="-1"
        style={{
          visibility: show ? "visible" : "hidden",
          background: "#f3f3f3",
          zIndex: 1046,
        }}
      >
        {/* Header Skeleton */}
        <div className="offcanvas-header" style={{ background: "#f3f3f3" }}>
          <div
            className="w-100 d-flex align-items-center justify-content-between fs-13"
            style={{ padding: "2px 7px 13px", color: "#0081d7" }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  ...shimmerStyle,
                  height: '30px',
                  width: '150px',
                  borderRadius: '2px',
                }}
              ></div>
            </div>
            <div>
              <div
                style={{
                  ...shimmerStyle,
                  height: '30px',
                  width: '30px',
                  borderRadius: '2px',
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="offcanvas-body pt-0" style={{ background: "#f3f3f3" }}>
          <Row className="ms-0">
            {/* Left Column Skeleton */}
            <Col
              md={7}
              className="ps-0 contactInformation"
              style={{ height: "96%", paddingRight: 0 }}
            >
              {/* Project Title Card */}
              <div
                className="me-2"
                style={{
                  background: "#fff",
                  paddingTop: "12px",
                  borderRadius: "5px",
                  border: "1px solid #e0e4f8",
                  marginBottom: "10px",
                }}
              >
                <div
                  className="d-flex gap-3 align-items-center mb-0 pb-0"
                  style={{ padding: "15px" }}
                >
                  <div className="w-100">
                    <div
                      style={{
                        ...shimmerStyle,
                        height: '28px',
                        width: '250px',
                        borderRadius: '4px',
                        marginBottom: '8px',
                      }}
                    ></div>
                    <div
                      style={{
                        ...shimmerStyle,
                        height: '16px',
                        width: '300px',
                        borderRadius: '4px',
                      }}
                    ></div>
                  </div>
                </div>
                <hr />
                <div className="pt-0 ps-0 pb-0 mt-3" style={{ background: "transparent" }}>
                  <Row className="w-100 ms-0 mb-2">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <Col key={item} md={4} className="mb-3 d-flex align-items-center">
                        <div
                          style={{
                            ...shimmerStyle,
                            width: '35px',
                            height: '35px',
                            borderRadius: '50%',
                            marginRight: '10px',
                          }}
                        ></div>
                        <div>
                          <div
                            style={{
                              ...shimmerStyle,
                              height: '12px',
                              width: '80px',
                              borderRadius: '4px',
                              marginBottom: '4px',
                            }}
                          ></div>
                          <div
                            style={{
                              ...shimmerStyle,
                              height: '14px',
                              width: '120px',
                              borderRadius: '4px',
                            }}
                          ></div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>

              {/* Associated Contact and Progress Chart Skeleton */}
              <Row className="w-100 ms-0 ps-0 pe-2 mb-2 contactDetailOffcanvas">
                {/* Associated Contact Card Skeleton */}
                <Col md={6} className="ps-0 pe-0">
                  <div
                    className="card"
                    style={{
                      background: "#fff",
                      paddingTop: "6px",
                      borderRadius: "5px",
                      height: '350px'
                    }}
                  >
                    <div className="card-header pb-1" style={{ marginLeft: "-1.2rem", marginRight: "-1.2rem" }}>
                      <div
                        style={{
                          ...shimmerStyle,
                          height: '20px',
                          width: '180px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                        }}
                      ></div>
                    </div>
                    <div className="pt-3">
                      {[1, 2, 3, 4].map((item) => (
                        <div key={item}>
                          <div className="d-flex gap-2 align-items-center mb-3">
                            <div
                              style={{
                                ...shimmerStyle,
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                marginRight: '10px',
                              }}
                            ></div>
                            <div className="w-100">
                              <div className="d-flex justify-content-between mb-1">
                                <div
                                  style={{
                                    ...shimmerStyle,
                                    height: '16px',
                                    width: '100px',
                                    borderRadius: '4px',
                                  }}
                                ></div>
                                <div className="d-flex gap-3">
                                  <div
                                    style={{
                                      ...shimmerStyle,
                                      height: '20px',
                                      width: '60px',
                                      borderRadius: '4px',
                                    }}
                                  ></div>
                                  <div
                                    style={{
                                      ...shimmerStyle,
                                      height: '20px',
                                      width: '40px',
                                      borderRadius: '4px',
                                    }}
                                  ></div>
                                </div>
                              </div>
                              <div
                                style={{
                                  ...shimmerStyle,
                                  height: '12px',
                                  width: '80px',
                                  borderRadius: '4px',
                                }}
                              ></div>
                            </div>
                          </div>
                          {item < 4 && <hr />}
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>

                {/* Project Progress Chart Skeleton */}
                <Col md={6} className="pe-0">
                  <div
                    className="card"
                    style={{
                      background: "#fff",
                      paddingTop: "6px",
                      borderRadius: "5px",
                      height: "97%",
                    }}
                  >
                    <div className="card-header pb-1" style={{ marginLeft: "-1.2rem", marginRight: "-1.2rem" }}>
                      <div
                        style={{
                          ...shimmerStyle,
                          height: '20px',
                          width: '150px',
                          borderRadius: '4px',
                          marginBottom: '8px',
                        }}
                      ></div>
                    </div>
                    <div className="card-body" style={{ height: "250px", width: "100%" }}>
                      <div
                        style={{
                          ...shimmerStyle,
                          height: '200px',
                          width: '100%',
                          borderRadius: '4px',
                        }}
                      ></div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>

            {/* Right Column Tabs Skeleton */}
            <Col
              md={5}
              style={{
                borderLeft: "1px solid #d1d4e3",
                position: "sticky",
                top: "0",
                overflowY: "auto",
                height: "calc(100vh - 6rem)",
                paddingLeft: ".5rem"
              }}
              className="pt-0"
            >
              <div className="bg-white h-100 cardBorders mb-0 rounded-2">
                {/* Tabs Header Skeleton */}
                <div className="card-header px-0 d-flex justify-content-between align-items-center py-0 cardHeaderBorders">
                  <div className="d-flex align-items-center gap-1 navForTasks w-100">
                    <div className="d-flex gap-2 w-100">
                      {[1, 2, 3, 4].map((tab) => (
                        <div
                          key={tab}
                          style={{
                            ...shimmerStyle,
                            height: '40px',
                            width: '80px',
                            borderRadius: '4px',
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="card-body px-0 pt-0" style={{ height: "90%" }}>
                  {/* Tasks Tab Content Skeleton */}
                  <div className="p-0 pt-2 h-100">
                    <div className="d-flex justify-content-between align-items-center mb-0 px-2">
                      <div>
                        <div
                          style={{
                            ...shimmerStyle,
                            height: '24px',
                            width: '80px',
                            borderRadius: '4px',
                            marginBottom: '4px',
                          }}
                        ></div>
                        <div
                          style={{
                            ...shimmerStyle,
                            height: '14px',
                            width: '200px',
                            borderRadius: '4px',
                          }}
                        ></div>
                      </div>
                      <div
                        style={{
                          ...shimmerStyle,
                          height: '30px',
                          width: '70px',
                          borderRadius: '4px',
                        }}
                      ></div>
                    </div>
                    <hr style={{ marginBottom: "0px" }} />
                    
                    {/* Timeline Tasks Skeleton */}
                    <div style={{ height: "90%", overflow: "auto" }}>
                      <div className="mt-3">
                        <div className="p-2 bg-white">
                          <div className="timeline">
                            {[1, 2, 3].map((stage, idx) => (
                              <div className="timeline-item d-flex" key={idx}>
                                {/* Timeline Dot Skeleton */}
                                <div className="timeline-center pe-2">
                                  <div
                                    style={{
                                      ...shimmerStyle,
                                      width: '24px',
                                      height: '24px',
                                      borderRadius: '50%',
                                    }}
                                  ></div>
                                  {idx < 2 && (
                                    <div
                                      style={{
                                        ...shimmerStyle,
                                        width: '2px',
                                        height: '80px',
                                        marginLeft: '11px',
                                        borderRadius: '2px',
                                      }}
                                    ></div>
                                  )}
                                </div>

                                {/* Timeline Content Skeleton */}
                                <div className="timeline-content flex-grow-1">
                                  <div className="d-flex justify-content-between mb-2">
                                    <div
                                      style={{
                                        ...shimmerStyle,
                                        height: '18px',
                                        width: '120px',
                                        borderRadius: '4px',
                                      }}
                                    ></div>
                                    <div
                                      style={{
                                        ...shimmerStyle,
                                        height: '18px',
                                        width: '50px',
                                        borderRadius: '4px',
                                      }}
                                    ></div>
                                  </div>

                                  {/* Task Items Skeleton */}
                                  {[1, 2].map((task) => (
                                    <div key={task} className="mb-2">
                                      <div className="d-flex align-items-center gap-2 mb-1">
                                        <div
                                          style={{
                                            ...shimmerStyle,
                                            height: '12px',
                                            width: '150px',
                                            borderRadius: '4px',
                                          }}
                                        ></div>
                                        <div
                                          style={{
                                            ...shimmerStyle,
                                            height: '12px',
                                            width: '60px',
                                            borderRadius: '4px',
                                          }}
                                        ></div>
                                        <div
                                          style={{
                                            ...shimmerStyle,
                                            height: '12px',
                                            width: '80px',
                                            borderRadius: '4px',
                                          }}
                                        ></div>
                                        <div
                                          style={{
                                            ...shimmerStyle,
                                            height: '16px',
                                            width: '16px',
                                            borderRadius: '50%',
                                          }}
                                        ></div>
                                        <div
                                          style={{
                                            ...shimmerStyle,
                                            height: '16px',
                                            width: '16px',
                                            borderRadius: '4px',
                                          }}
                                        ></div>
                                      </div>
                                      <div
                                        style={{
                                          ...shimmerStyle,
                                          height: '1px',
                                          width: '100%',
                                          margin: '8px 0',
                                          borderRadius: '2px',
                                        }}
                                      ></div>
                                    </div>
                                  ))}

                                  {/* Show More Button Skeleton */}
                                  <div className="mb-3">
                                    <div
                                      style={{
                                        ...shimmerStyle,
                                        height: '16px',
                                        width: '100px',
                                        borderRadius: '4px',
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default ProjectDetailSkeleton;